import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    ProjectItemCategoryBreadcrumbs,
    ProjectItemCategoryCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsProjectsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as ProjectItemTemplateCategoryActions from "../../../actions/projectItemTemplateCategory";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { ProjectLookupsLayout } from "../../index";

import "./ProjectItemTemplateCategoryPage.scss";

interface ProjectItemTemplateCategoryPageProps {
    form: Dtos.ProjectItemTemplateCategory & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    projectItemTemplateCategory: Dtos.ProjectItemTemplateCategory;
    lookups: Dtos.Lookup[];
    loadingProjectItemTemplateCategory: boolean;
    loadProjectItemTemplateCategoryFailure: boolean;
    loadProjectItemTemplateCategoryErrorCode: string;
    savingProjectItemTemplateCategory: boolean;
    saveProjectItemTemplateCategorySuccess: boolean;
    saveProjectItemTemplateCategoryFailure: boolean;
    creatingProjectItemTemplateCategory: boolean;
    createProjectItemTemplateCategorySuccess: boolean;
    validatingProjectItemTemplateCategory: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearProjectItemTemplateCategory: ProjectItemTemplateCategoryActions.IProjectItemTemplateCategoryClearActionCreator;
    loadProjectItemTemplateCategory: ProjectItemTemplateCategoryActions.IProjectItemTemplateCategoryLoadByIdActionCreator;
    saveProjectItemTemplateCategory: ProjectItemTemplateCategoryActions.IProjectItemTemplateCategorySaveActionCreator;
    createProjectItemTemplateCategory: ProjectItemTemplateCategoryActions.IProjectItemTemplateCategoryCreateActionCreator;
    validateProjectItemTemplateCategory: ProjectItemTemplateCategoryActions.IProjectItemTemplateCategoryValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.projectItemTemplateCategory";

const ProjectItemTemplateCategoryPage: FC<
    ProjectItemTemplateCategoryPageProps
> = ({
    form,
    formState,
    formProperties,
    projectItemTemplateCategory,
    lookups,
    loadingProjectItemTemplateCategory,
    loadProjectItemTemplateCategoryFailure,
    loadProjectItemTemplateCategoryErrorCode,
    savingProjectItemTemplateCategory,
    saveProjectItemTemplateCategorySuccess,
    saveProjectItemTemplateCategoryFailure,
    creatingProjectItemTemplateCategory,
    createProjectItemTemplateCategorySuccess,
    validatingProjectItemTemplateCategory,
    validationFailures,
    permissions,
    clearProjectItemTemplateCategory,
    loadProjectItemTemplateCategory,
    saveProjectItemTemplateCategory,
    createProjectItemTemplateCategory,
    validateProjectItemTemplateCategory,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { projectItemTemplateCategoryId } = useParams<{
        projectItemTemplateCategoryId: string;
    }>();
    const projectItemTemplateCategoryIdNumber = parseInt(
        projectItemTemplateCategoryId,
        10
    );

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.ProjectItemTemplateCategory =
            {} as Dtos.ProjectItemTemplateCategory;

        loadForm(
            reduxFormName,
            Object.assign({ ...projectItemTemplateCategory }, extraData)
        );
    };


    // Load project item template category
    useEffect(() => {
        if (
            projectItemTemplateCategoryIdNumber &&
            !projectItemTemplateCategory &&
            !loadingProjectItemTemplateCategory
        ) {
            setDrawerItems(SettingsProjectsSubMenu(permissions));
            setBreadcrumbs(
                ProjectItemCategoryBreadcrumbs(
                    projectItemTemplateCategoryIdNumber
                )
            );
            loadProjectItemTemplateCategory(
                projectItemTemplateCategoryIdNumber
            );
        } else if (
            !projectItemTemplateCategoryIdNumber &&
            !creatingProjectItemTemplateCategory &&
            !createProjectItemTemplateCategorySuccess
        ) {
            createProjectItemTemplateCategory();
            setBreadcrumbs(ProjectItemCategoryCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (projectItemTemplateCategory && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        projectItemTemplateCategoryIdNumber,
        projectItemTemplateCategory,
        setDrawerItems,
        SettingsProjectsSubMenu,
        permissions,
        setBreadcrumbs,
        ProjectItemCategoryBreadcrumbs,
        loadProjectItemTemplateCategory,
        createProjectItemTemplateCategory,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingProjectItemTemplateCategory,
        creatingProjectItemTemplateCategory,
        createProjectItemTemplateCategorySuccess,
        ProjectItemCategoryCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadProjectItemTemplateCategoryFailure) {
            navigate(`/error/${loadProjectItemTemplateCategoryErrorCode}`);
        }
    }, [
        loadProjectItemTemplateCategoryFailure,
        loadProjectItemTemplateCategoryErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            projectItemTemplateCategoryIdNumber &&
            projectItemTemplateCategory?.value
        ) {
            setBreadcrumbs(
                ProjectItemCategoryBreadcrumbs(
                    projectItemTemplateCategoryIdNumber,
                    projectItemTemplateCategory?.value
                )
            );
        }
    }, [
        projectItemTemplateCategoryIdNumber,
        projectItemTemplateCategory,
        setBreadcrumbs,
        ProjectItemCategoryBreadcrumbs,
    ]);

    // Reload project item template category after saving
    useEffect(() => {
        if (
            !savingProjectItemTemplateCategory &&
            saveProjectItemTemplateCategorySuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Project item template category saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/project/itemcategories";
                navigate(url);
            } else if (!projectItemTemplateCategoryIdNumber) {
                let url: string = `/settings/project/itemcategories/${projectItemTemplateCategory?.id}`;
                navigate(url);
                clearProjectItemTemplateCategory();
            }
        }
    }, [
        savingProjectItemTemplateCategory,
        saveProjectItemTemplateCategorySuccess,
        projectItemTemplateCategoryIdNumber,
        navigate,
        clearProjectItemTemplateCategory,
        saveAndReturn,
        projectItemTemplateCategory,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingProjectItemTemplateCategory &&
            saveProjectItemTemplateCategoryFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingProjectItemTemplateCategory,
        saveProjectItemTemplateCategoryFailure,
    ]);

    // Cleanup effect to clear the project item template category on component unmount
    useEffect(() => {
        return () => {
            clearProjectItemTemplateCategory();
            resetForm(reduxFormName);
        };
    }, [clearProjectItemTemplateCategory, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/project/itemcategories";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.ProjectItemTemplateCategory) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.ProjectItemTemplateCategory =
                {} as Dtos.ProjectItemTemplateCategory;

            await saveProjectItemTemplateCategory(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.ProjectItemTemplateCategory =
                {} as Dtos.ProjectItemTemplateCategory;

            if (
                form &&
                hasBeenSubmitted &&
                !validatingProjectItemTemplateCategory
            ) {
                await validateProjectItemTemplateCategory(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.ProjectItemCategoriesUpdate,
                Dtos.Permission.ProjectItemCategoriesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingProjectItemTemplateCategory ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "projectItemTemplateCategory",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "projectItemTemplateCategory",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "projectItemTemplateCategory",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "projectItemTemplateCategory",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingProjectItemTemplateCategory,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <ProjectLookupsLayout
            loading={loadingProjectItemTemplateCategory}
            permission={Dtos.Permission.ProjectItemCategoriesView}
        >
            {renderContent}
        </ProjectLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<ProjectItemTemplateCategoryPageProps> => {
    let projectItemTemplateCategory:
        | Dtos.ProjectItemTemplateCategory
        | undefined = !(
        state.projectItemTemplateCategorys.formData instanceof Array
    )
        ? state.projectItemTemplateCategorys.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        projectItemTemplateCategory: projectItemTemplateCategory!,
        loadingProjectItemTemplateCategory:
            state.projectItemTemplateCategorys.loadState &&
            state.projectItemTemplateCategorys.loadState.status ==
                RequestState.Pending,
        loadProjectItemTemplateCategoryFailure:
            state.projectItemTemplateCategorys.loadState &&
            state.projectItemTemplateCategorys.loadState.status ==
                RequestState.Failure,
        loadProjectItemTemplateCategoryErrorCode:
            state.projectItemTemplateCategorys.loadState.errorCode,
        savingProjectItemTemplateCategory:
            state.projectItemTemplateCategorys.saveState &&
            state.projectItemTemplateCategorys.saveState.status ==
                RequestState.Pending,
        saveProjectItemTemplateCategorySuccess:
            state.projectItemTemplateCategorys.saveState &&
            state.projectItemTemplateCategorys.saveState.status ==
                RequestState.Success,
        saveProjectItemTemplateCategoryFailure:
            state.projectItemTemplateCategorys.saveState &&
            state.projectItemTemplateCategorys.saveState.status ==
                RequestState.Failure,
        creatingProjectItemTemplateCategory:
            state.projectItemTemplateCategorys.createState &&
            state.projectItemTemplateCategorys.createState.status ==
                RequestState.Pending,
        createProjectItemTemplateCategorySuccess:
            state.projectItemTemplateCategorys.createState &&
            state.projectItemTemplateCategorys.createState.status ==
                RequestState.Success,
        validatingProjectItemTemplateCategory:
            state.projectItemTemplateCategorys.validateState &&
            state.projectItemTemplateCategorys.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.projectItemTemplateCategory,
        formState: state.projectItemTemplateCategorys.formState,
        formProperties: state.projectItemTemplateCategorys.formProperties,
        validationFailures: projectItemTemplateCategory
            ? state.projectItemTemplateCategorys.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<ProjectItemTemplateCategoryPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadProjectItemTemplateCategory: bindActionCreators(
            ProjectItemTemplateCategoryActions.LoadProjectItemTemplateCategoryById,
            dispatch
        ),
        clearProjectItemTemplateCategory: bindActionCreators(
            ProjectItemTemplateCategoryActions.Clear,
            dispatch
        ),
        saveProjectItemTemplateCategory: bindActionCreators(
            ProjectItemTemplateCategoryActions.SaveProjectItemTemplateCategory,
            dispatch
        ),
        createProjectItemTemplateCategory: bindActionCreators(
            ProjectItemTemplateCategoryActions.CreateProjectItemTemplateCategory,
            dispatch
        ),
        validateProjectItemTemplateCategory: bindActionCreators(
            ProjectItemTemplateCategoryActions.ValidateProjectItemTemplateCategory,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ProjectItemTemplateCategoryPage);
